import {
  Box,
  Button,
  ButtonGroup,
  Fab,
  Grid,
  Pagination,
  styled,
  Typography,
} from '@mui/material';
import LayoutBase from '../../shared/layouts';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { api } from '../../shared/services/api';
import Cookies from 'js-cookie';
import UndrawNoData from '../../assets/svg/undraw_no_data.svg';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import CardCss0001 from './components/cardCcs0001';
import ModalDetail from './components/modalDetails';
import { IResponseCCS0001 } from '../../shared/services/types/response';
import InputSearch from './components/inputSearch';
import Loading from '../../components/loading';

const filtersObject = {
  Todos: 'ALL',
  Criado: 'CREATED',
  Pendente: 'PENDING',
  Deferido: 'DONE',
  Cancelado: 'CANCEL',
  Recusado: 'REFUSED',
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const CCS0001 = () => {
  const token = Cookies.get('@token');
  const [ccs0001Data, setCcs0001Data] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IResponseCCS0001 | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(0);
  const [filter, setFilter] = useState<string>('ALL');
  const [cpf, setCpf] = useState<string>('');
  const itemsPerPage = 8;

  // Atualiza a página ao mudar o filtro ou a página
  useEffect(() => {
    handleGetAll();
  }, [currentPage, filter, cpf]);

  const handleChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reseta a paginação ao alterar o filtro
  };

  const handleFilterCPFChange = (newFilter: string) => {
    // Remove qualquer caractere não numérico (como pontos, traços, barras, etc.)
    const onlyNumbers = newFilter.replace(/\D/g, '');

    setCpf(onlyNumbers); // Define o estado com apenas os números
    setCurrentPage(1); // Reseta a paginação ao alterar o filtro
  };

  const handleClearFilterCPFChange = () => {
    setCpf('');
    setCurrentPage(1); // Reseta a paginação ao alterar o filtro
  };

  const handleGetAll = async () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        limit: itemsPerPage,
        page: currentPage,
        status: filter !== 'ALL' ? filter : undefined, // Envia filtro apenas se não for "ALL"
        ...(cpf !== '' && { cpf_cnpj_person: cpf }), // Só adiciona cpf_cnpj_person se cpf não for vazio
      },
    };

    try {
      setIsLoading(true);
      const responseDetailPerson = await api.get(
        '/detailPerson/getAll',
        config,
      );

      const {
        data,
        meta: { totalPages },
      } = responseDetailPerson.data;

      setCcs0001Data(data || []);
      setPages(totalPages || 1);
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  const handleUploadCcsArchive = async (files: FileList | null) => {
    setIsUploadLoading(true);

    if (!files || files?.length === 0) {
      setIsUploadLoading(false);
      return;
    }

    if (files[0].type != 'text/xml') {
      toast.warn('Tipo de arquivo inválido');
      setIsUploadLoading(false);
      return;
    }

    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    };

    const formDataBody = new FormData();
    const ccsArchive = files?.[0];

    formDataBody.append('file', ccsArchive);
    try {
      const responseUploadDetailPerson = await api.post(
        '/archive/upload-detail-person',
        formDataBody,
        config,
      );

      if (responseUploadDetailPerson.status === 201) {
        toast.success('Upload feito com sucesso');
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      handleGetAll();
      setIsUploadLoading(false);
    }
    setIsUploadLoading(false);
  };

  const handleOpenModal = (item: IResponseCCS0001) => {
    setSelectedItem(item);
  };

  return (
    <LayoutBase
      footer={
        isLoading || ccs0001Data.length === 0 ? null : (
          <Pagination
            count={pages}
            page={currentPage}
            onChange={handleChange}
            color="primary"
          />
        )
      }
    >
      <Typography color="textPrimary" variant="h5">
        CCS0001
      </Typography>
      <Typography sx={{ marginBottom: 2 }}>
        Arquivo CSS0001 (arquivo de atualização de clientes) : Insira um arquivo
        CCS0001 para que as respostas sejam geradas.
      </Typography>

      <Grid
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: {
            xs: 'column', // Mobile: empilha em coluna
            sm: 'row', // A partir do "sm": em linha (padrão)
          },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Fab
            component="label"
            size="medium"
            color="secondary"
            aria-label="add"
            disabled={isUploadLoading}
          >
            <AddIcon />
            <VisuallyHiddenInput
              type="file"
              onChange={(event) => handleUploadCcsArchive(event.target.files)}
              multiple
            />
          </Fab>

          <ButtonGroup
            variant="outlined"
            color="primary"
            // sx={{ marginLeft: 2 }}
          >
            {Object.entries(filtersObject).map(([label, value]) => (
              <Button
                key={value}
                variant={filter === value ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange(value)}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        <InputSearch
          label="Pesquise pelo CPF ou CNPJ"
          onSearch={handleFilterCPFChange}
          handleClearInput={handleClearFilterCPFChange}
        />
      </Grid>

      <Loading isLoading={isLoading} />
      {ccs0001Data.length === 0 && !isLoading ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              component="img"
              src={UndrawNoData}
              alt="Svg em construção"
              sx={{
                maxWidth: '15%',
                height: 'auto',
                marginTop: 10,
              }}
            />
          </Box>
          <Typography textAlign={'center'} sx={{ marginTop: 2 }}>
            Dados não encontrados...
          </Typography>
        </>
      ) : (
        <Box sx={{ padding: 3 }}>
          <Grid container spacing={2}>
            {ccs0001Data.map((item: IResponseCCS0001) => (
              <>
                <CardCss0001
                  key={item.id}
                  ccsValue={item}
                  handleOpenModal={() => handleOpenModal(item)}
                />
              </>
            ))}
          </Grid>
          <ModalDetail
            open={selectedItem !== null}
            ccsValue={selectedItem}
            closeModal={(shouldReload?: boolean) => {
              setSelectedItem(null);
              if (shouldReload) handleGetAll();
            }}
          />
        </Box>
      )}
    </LayoutBase>
  );
};

export default CCS0001;
